import {
  ReviewFinancialSummary,
  ReviewFlexMonthlySummary,
  ReviewLoanSummary,
  ReviewMonthlySummary,
} from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { FLEX_ADDITIONAL_ENERGY_PAYMENT_TOOLTIP } from "./offerReviewPaymentCopy";
import { formatCurrency } from "utils/formatHelpers";

const OFFER_REVIEW_FLEX_MAX_HIDDEN_STATES = new Set(["TX", "MA", "HI"]);

const SIMPLIFIED_OFFER_REVIEW_PAYMENT_DISCLAIMER =
  "Includes 5% ACH discount and does not include applicable taxes.";

type FlexActionBarPaymentDisplay = {
  primaryLine: string;
  secondaryLine: string;
};

const getFlexYear1MarginalRatePerKwh = (
  summary: ReviewFlexMonthlySummary
): number => {
  const marginalRateValue = summary.rates.find(
    (r) => r.kind === "Marginal"
  )?.value;
  if (marginalRateValue != null) {
    const parsed = Number.parseFloat(marginalRateValue);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  const consumptionRate = Number.parseFloat(
    summary.rates.find((r) => r.kind === "Consumption")?.value ?? "0"
  );
  const productionRate = Number.parseFloat(
    summary.rates.find((r) => r.kind === "Production")?.value ?? "0"
  );
  return consumptionRate - productionRate;
};

const shouldHideFlexMaxOnOfferReview = (state: string) =>
  OFFER_REVIEW_FLEX_MAX_HIDDEN_STATES.has(state?.toUpperCase());

const getFlexAdditionalAnnualKwhDelta = (
  consumption: number,
  production: number
): string => (production - consumption).toLocaleString();

const getFlexYear1AdditionalEnergyTooltip = (
  summary: ReviewFlexMonthlySummary,
  consumption: number,
  production: number
): string => {
  if (shouldHideFlexMaxOnOfferReview(summary.state)) {
    return FLEX_ADDITIONAL_ENERGY_PAYMENT_TOOLTIP;
  }
  const formattedDelta = getFlexAdditionalAnnualKwhDelta(
    consumption,
    production
  );
  return `Flex has additional discounted electricity ready for your needs, and you only pay for what you use. Your maximum monthly bill would be ${formatCurrency(
    summary.maximumMonthlyPayment,
    2
  )}, if you used the additional ${formattedDelta} kWh available.`;
};

const getFlexActionBarPaymentDisplay = (
  summary: ReviewFlexMonthlySummary
): FlexActionBarPaymentDisplay => {
  const totalPayment = formatCurrency(summary.minimumMonthlyPayment, 2);
  const primaryLine = `Year 1 Total Sunrun Payment ${totalPayment}/mo`;

  const secondaryLine =
    summary.state === "HI"
      ? `Flex up with a predictable year 1 rate of ${formatCurrency(
          getFlexYear1MarginalRatePerKwh(summary),
          3
        )} kWh`
      : `Flex opportunity up to ${formatCurrency(
          summary.maximumMonthlyPayment,
          2
        )}/mo`;

  return { primaryLine, secondaryLine };
};

const getOfferReviewSummaryMonthlyPayment = (
  summary: ReviewFinancialSummary
): number | undefined => {
  switch (summary.type) {
    case "Monthly":
      return (summary as ReviewMonthlySummary).firstYearMonthly;
    case "FlexMonthly":
      return (summary as ReviewFlexMonthlySummary).minimumMonthlyPayment;
    case "Loan":
      return (summary as ReviewLoanSummary).monthlyWithVoluntary;
    default:
      return undefined;
  }
};

export {
  getFlexActionBarPaymentDisplay,
  getFlexAdditionalAnnualKwhDelta,
  getFlexYear1AdditionalEnergyTooltip,
  getFlexYear1MarginalRatePerKwh,
  getOfferReviewSummaryMonthlyPayment,
  OFFER_REVIEW_FLEX_MAX_HIDDEN_STATES,
  shouldHideFlexMaxOnOfferReview,
  SIMPLIFIED_OFFER_REVIEW_PAYMENT_DISCLAIMER,
};
export type { FlexActionBarPaymentDisplay };
